import { useForm } from '@mantine/form';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { EXPENSE_TYPE_TREE, MainCategory } from 'src/Constants/expenseCategorization';
import { FormAttachment } from 'src/Hooks/Forms/types/formAttachment.types';
import { transformFormValuesToRecurrenceDto } from 'src/Hooks/Forms/UseCreateEventForm/transformFormValuesToRecurrenceDto';
import { defaultRecurrenceValue } from 'src/Hooks/Forms/UseCreateEventForm/useCreateEventForm';
import { RecurrenceValues } from 'src/Hooks/Forms/UseCreateEventForm/useCreateEventForm.types';
import useFetchMembers from 'src/Hooks/Queries/useFetchMembers';
import {
  CreateExpenseDto,
  CreateExpenseDtoCurrencyEnum,
  CreateExpenseDtoSplitTypeEnum,
  CreateExpenseDtoTypeEnum,
} from 'src/Services/API/generated';

// Mirrors backend's SplitScope enum until `npm run generate:api` is re-run after merge.
export enum CreateExpenseFormSplitScope {
  INDIVIDUAL = 'INDIVIDUAL',
  HOUSEHOLD = 'HOUSEHOLD',
}

export interface CreateExpenseHouseholdShare {
  selected: boolean;
  householdId: string;
  amount: number;
  percentage: number;
}

export interface CreateExpenseFormValues {
  title: string;
  amount?: number;
  currency: CreateExpenseDtoCurrencyEnum;
  type?: CreateExpenseDtoTypeEnum;
  paidBy?: string; // A composite key like 'user-uuid', 'bank-uuid', 'contact-uuid' or 'placeholder-uuid'
  fundCallPayerId?: string; // Member userId credited when `type` is a fund call category
  paidAt: Date;
  isIncome: boolean;
  imagePath?: string;
  imageUrl?: string;
  splitType: CreateExpenseDtoSplitTypeEnum;
  splitScope: CreateExpenseFormSplitScope;
  /** `participantId` is a user id, or a PlaceholderMember id when `isPlaceholder`. */
  shares: {
    selected: boolean;
    participantId: string;
    isPlaceholder: boolean;
    amount: number;
    percentage: number;
  }[];
  householdShares: CreateExpenseHouseholdShare[];
  attachments: FormAttachment[];
  merchant: string;
  note?: string;
  reimburseToBankAccount?: string;
  recurrence: RecurrenceValues;
  locativeShare?: number;
}

interface UseCreateExpenseFormOptions {
  defaultIsIncome?: boolean;
}

export const useCreateExpenseForm = (options?: UseCreateExpenseFormOptions) => {
  const { t } = useTranslation();
  const { data: members } = useFetchMembers();

  const form = useForm<
    CreateExpenseFormValues,
    (values: CreateExpenseFormValues) => CreateExpenseDto
  >({
    initialValues: {
      title: '',
      merchant: '',
      note: '',
      currency: CreateExpenseDtoCurrencyEnum.Eur,
      splitType: CreateExpenseDtoSplitTypeEnum.Equal,
      splitScope: CreateExpenseFormSplitScope.INDIVIDUAL,
      type: undefined,
      shares: [],
      householdShares: [],
      isIncome: options?.defaultIsIncome ?? false,
      fundCallPayerId: undefined,
      paidAt: new Date(),
      attachments: [],
      reimburseToBankAccount: '',
      recurrence: defaultRecurrenceValue,
      locativeShare: undefined,
    },
    validate: {
      title: (value: string) =>
        value.length < 3
          ? t('forms.expense.invalidTitle', 'Le titre doit faire au moins 3 caractères')
          : null,
      amount: (value?: number) =>
        value === undefined || value <= 0
          ? t('forms.expense.negativeAmount', 'Le montant doit être supérieur à 0')
          : null,
      type: (value?: CreateExpenseDtoTypeEnum) =>
        !value ? t('forms.expense.categoryRequired', 'Choisissez une catégorie') : null,
      paidBy: (value: string | undefined, values: CreateExpenseFormValues) => {
        const missingMessage = values.isIncome
          ? t('forms.expense.undefinedReceivedBy', 'Indiquez qui a encaissé')
          : t('forms.expense.undefinedPaidBy', 'Indiquez qui a payé');
        if (!value) return missingMessage;
        // Guards against stray values like 'user-undefined' slipping through in edition.
        return /^(user|bank|contact|placeholder)-[0-9a-fA-F-]{36}$/.test(value)
          ? null
          : missingMessage;
      },
      fundCallPayerId: (value: string | undefined, values: CreateExpenseFormValues) => {
        const isFundCall =
          !!values.type && EXPENSE_TYPE_TREE[values.type]?.main === MainCategory.FundingCalls;
        return isFundCall && !value
          ? t('expense.form.fundCallPayerRequired', 'Le payeur doit être précisé')
          : null;
      },
      shares: (
        value: {
          selected: boolean;
          participantId: string;
          isPlaceholder: boolean;
          amount: number;
          percentage: number;
        }[],
        values
      ) => {
        if (values.splitScope === CreateExpenseFormSplitScope.HOUSEHOLD) return null;
        if (value.filter(v => v.selected).length === 0)
          return t('forms.expense.noShare', 'Vous devez sélectionner au moins une personne');

        if (values.splitType === CreateExpenseDtoSplitTypeEnum.Amount) {
          if (
            value.filter(s => s.selected).reduce((prev, current) => prev + current.amount, 0) !==
            values.amount
          )
            return t(
              'forms.expense.wrongSum',
              'La somme des montants doit être égale au montant total'
            );

          if (value.filter(s => s.selected).some(s => s.amount === 0))
            return t('forms.expense.amountNull', 'Les montants doivent être plus grands que zéro');
        }

        if (values.splitType === CreateExpenseDtoSplitTypeEnum.Percentage) {
          if (
            value
              .filter(s => s.selected)
              .reduce((prev, current) => prev + current.percentage, 0) !== 100
          )
            return t(
              'forms.expense.wrongPercentageSum',
              'La somme des pourcentages doit être égale à 100'
            );

          if (value.filter(s => s.selected).some(s => s.percentage === 0))
            return t(
              'forms.expense.percentageNull',
              'Les pourcentages doivent être plus grands que zéro'
            );
        }
      },
      householdShares: (value: CreateExpenseHouseholdShare[], values) => {
        if (values.splitScope !== CreateExpenseFormSplitScope.HOUSEHOLD) return null;
        const selected = value.filter(v => v.selected);
        if (selected.length === 0)
          return t('forms.expense.noHouseholdShare', 'Sélectionne au moins un foyer');

        if (values.splitType === CreateExpenseDtoSplitTypeEnum.Amount) {
          const sum = selected.reduce((prev, current) => prev + current.amount, 0);
          if (sum !== values.amount)
            return t(
              'forms.expense.wrongSum',
              'La somme des montants doit être égale au montant total'
            );
          if (selected.some(s => s.amount === 0))
            return t('forms.expense.amountNull', 'Les montants doivent être plus grands que zéro');
        }
        if (values.splitType === CreateExpenseDtoSplitTypeEnum.Percentage) {
          const sum = selected.reduce((prev, current) => prev + current.percentage, 0);
          if (sum !== 100)
            return t(
              'forms.expense.wrongPercentageSum',
              'La somme des pourcentages doit être égale à 100'
            );
          if (selected.some(s => s.percentage === 0))
            return t(
              'forms.expense.percentageNull',
              'Les pourcentages doivent être plus grands que zéro'
            );
        }
        return null;
      },
    },
    transformValues: (values: CreateExpenseFormValues) => {
      const {
        attachments,
        paidBy: formPaidBy,
        recurrence,
        fundCallPayerId: _fundCallPayerId,
        ...formValue
      } = values;
      void _fundCallPayerId;

      let paidByUserId: string | undefined;
      let paidByBankAccountId: string | undefined;
      let paidByContactId: string | undefined;
      let paidByPlaceholderMemberId: string | undefined;

      if (formPaidBy?.startsWith('bank-')) {
        paidByBankAccountId = formPaidBy.replace('bank-', '');
      } else if (formPaidBy?.startsWith('contact-')) {
        paidByContactId = formPaidBy.replace('contact-', '');
      } else if (formPaidBy?.startsWith('placeholder-')) {
        paidByPlaceholderMemberId = formPaidBy.replace('placeholder-', '');
      } else if (formPaidBy?.startsWith('user-')) {
        paidByUserId = formPaidBy.replace('user-', '');
      }

      if (!paidByUserId && !paidByBankAccountId && !paidByContactId && !paidByPlaceholderMemberId) {
        throw new Error('Payer could not be determined.');
      }

      const isByHousehold = values.splitScope === CreateExpenseFormSplitScope.HOUSEHOLD;

      const shares = isByHousehold
        ? undefined
        : values.shares
            .filter(s => s.selected)
            .map(share => ({
              // The API takes exactly one of the two ids.
              ...(share.isPlaceholder
                ? { placeholderMemberId: share.participantId }
                : { userId: share.participantId }),
              ...(values.splitType === CreateExpenseDtoSplitTypeEnum.Amount && {
                amount: share.amount,
              }),
              ...(values.splitType === CreateExpenseDtoSplitTypeEnum.Percentage && {
                percentage: share.percentage,
              }),
            }));

      const householdShares = isByHousehold
        ? values.householdShares
            .filter(s => s.selected)
            .map(share => ({
              householdId: share.householdId,
              ...(values.splitType === CreateExpenseDtoSplitTypeEnum.Amount && {
                amount: share.amount,
              }),
              ...(values.splitType === CreateExpenseDtoSplitTypeEnum.Percentage && {
                percentage: share.percentage,
              }),
            }))
        : undefined;

      const { householdShares: _hs, splitScope, ...rest } = formValue;
      void _hs;

      return {
        ...rest,
        shares,
        // Passed through to the API; will be picked up by the generated DTO after generate:api.
        ...(householdShares && { householdShares }),
        splitScope,
        paidBy: paidByUserId,
        paidByBankAccountId,
        paidByContactId,
        paidByPlaceholderMemberId,
        reimburseToBankAccount: values.reimburseToBankAccount
          ? values.reimburseToBankAccount.replace('bank-', '')
          : undefined,
        amount: values.amount!,
        paidAt: dayjs(values.paidAt),
        // Validation guarantees a category is picked before this transformer runs.
        type: values.type!,
        note: values.note?.trim() ? values.note.trim() : undefined,
        attachmentIds: attachments
          .map(attachment => attachment.fileId)
          .filter(id => id !== undefined),
        recurrence: transformFormValuesToRecurrenceDto(recurrence) ?? undefined,
      } as CreateExpenseDto;
    },
  });

  useEffect(() => {
    if (form.values.shares.length === 0) {
      form.setValues({
        shares:
          members?.members.map(member => ({
            amount: 0,
            percentage: 0,
            selected: true,
            // For an unregistered co-owner the API serves the PlaceholderMember id here.
            participantId: member.user.id,
            isPlaceholder: member.isPlaceholder,
          })) ?? [],
      });
    }
  }, [form, members]);

  return { form };
};
